import React from "react";
import type {
  AdditionalResults,
  CareContext,
  DiagnosisRow,
  FaceSheetPayload,
  ImagingRow,
  LabRow,
  MedicationRow,
  PatientSnapshot,
  ResultRow,
} from "./types";
import { formatDate, formatMedicationName, formatPhone, sortLabRowsOldestToNewest } from "./utils";

const LAB_COLUMNS = [
  { key: "date", label: "DATE" },
  { key: "calcium", label: "CALCIUM (mg/dL)" },
  { key: "ionized_calcium", label: "IONIZED CA (mg/dL)" },
  { key: "pth", label: "PTH (pg/mL)" },
  { key: "gfr", label: "GFR" },
  { key: "creatinine", label: "CREAT (mg/dL)" },
  { key: "vitamin_d", label: "VIT D (ng/mL)" },
  { key: "albumin", label: "ALBUMIN (g/dL)" },
] as const satisfies ReadonlyArray<{ key: keyof LabRow; label: string }>;

const EMPTY_LAB_ROW: LabRow = {
  date: "",
  calcium: "",
  ionized_calcium: "",
  reference_range: "",
  pth: "",
  pth_reference_range: "",
  gfr: "",
  creatinine: "",
  vitamin_d: "",
  albumin: "",
};

const EMPTY_RESULT_ROW: ResultRow = {
  date: "",
  value: "",
  reference_range: "",
};

const EMPTY_IMAGING_ROW: ImagingRow = {
  date: "",
  image_type: "",
  results: "",
};

function present(value: unknown): string {
  return value == null ? "" : String(value).trim();
}

function formattedCellValue(value: unknown, key?: string): string {
  if (key === "date" && typeof value === "string") return present(formatDate(value));
  return present(value);
}

function padRows<T>(rows: T[], minimum: number, hasContent: (row: T) => boolean, emptyRow: () => T): T[] {
  const filledRows = rows.filter(hasContent);
  return filledRows.length >= minimum
    ? filledRows
    : [...filledRows, ...Array.from({ length: minimum - filledRows.length }, emptyRow)];
}

function diagnosisColumns(diagnoses: DiagnosisRow[]): [DiagnosisRow[], DiagnosisRow[]] {
  const midpoint = Math.ceil(diagnoses.length / 2);
  return [diagnoses.slice(0, midpoint), diagnoses.slice(midpoint)];
}

function medicationText(medication: MedicationRow): string {
  return [formatMedicationName(medication), present(medication.dose), present(medication.frequency)].filter(Boolean).join(" ");
}

function dobAge(snapshot: PatientSnapshot): string {
  const dateOfBirth = present(snapshot.date_of_birth) ? formatDate(snapshot.date_of_birth) : "";
  const age = present(snapshot.age);

  if (dateOfBirth && age) return `${dateOfBirth} (${age})`;
  return dateOfBirth || age;
}

function cityState(snapshot: PatientSnapshot): string {
  return [present(snapshot.city), present(snapshot.state)].filter(Boolean).join(", ");
}

function textOkValue(careContext: CareContext): string {
  const value = present(careContext.text_ok);
  return value ? value.toUpperCase() : "";
}

function rowHasValues<T extends Record<string, unknown>>(row: T, keys: Array<keyof T>): boolean {
  return keys.some((key) => present(row[key]).length > 0);
}

interface DiagnosisColumnProps {
  diagnoses: DiagnosisRow[];
}

function DiagnosisColumn({ diagnoses }: DiagnosisColumnProps) {
  return (
    <table className="face-sheet-print-table">
      <colgroup>
        <col style={{ width: "18%" }} />
        <col style={{ width: "18%" }} />
        <col style={{ width: "64%" }} />
      </colgroup>
      <tbody>
        {diagnoses.map((diagnosis, index) => (
          <tr key={`${diagnosis.code || "diagnosis"}-${index}`}>
            <td>{diagnosis.code ? (diagnosis.selected ? "TRUE" : "FALSE") : ""}</td>
            <td className="font-semibold">{present(diagnosis.code)}</td>
            <td className="font-semibold">{present(diagnosis.label)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

interface ResultsTableProps {
  title: string;
  rows: ResultRow[];
}

function ResultsTable({ title, rows }: ResultsTableProps) {
  const paddedRows = padRows(
    rows,
    2,
    (row) => rowHasValues(row as Record<string, unknown>, ["date", "value", "reference_range"]),
    () => ({ ...EMPTY_RESULT_ROW })
  );

  return (
    <table className="face-sheet-print-table">
      <thead>
        <tr>
          <th className="face-sheet-print-head">DATE</th>
          <th className="face-sheet-print-head">{title}</th>
          <th className="face-sheet-print-head">REF. RANGE</th>
        </tr>
      </thead>
      <tbody>
        {paddedRows.map((row, index) => (
          <tr key={`${title}-${index}`}>
            <td>{present(formatDate(row.date))}</td>
            <td>{present(row.value)}</td>
            <td>{present(row.reference_range)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

interface FaceSheetPrintViewProps {
  payload?: FaceSheetPayload;
}

export default function FaceSheetPrintView({ payload = {} }: FaceSheetPrintViewProps) {
  const patientSnapshot = payload.patient_snapshot || {};
  const careContext = payload.care_context || {};
  const medications = padRows(
    payload.medications || [],
    12,
    (row) => medicationText(row).length > 0,
    () => ({ name: "", dose: "", frequency: "" })
  );
  const labs = padRows(
    sortLabRowsOldestToNewest(payload.labs),
    9,
    (row) =>
      rowHasValues(row as Record<string, unknown>, [
        "date",
        "calcium",
        "ionized_calcium",
        "pth",
        "gfr",
        "creatinine",
        "vitamin_d",
        "albumin",
      ]),
    () => ({ ...EMPTY_LAB_ROW })
  );
  const operationSummary = payload.operation_summary;
  const additionalResults: AdditionalResults = payload.additional_results || {};
  const imaging = padRows(
    payload.imaging || [],
    6,
    (row) => rowHasValues(row as Record<string, unknown>, ["date", "image_type", "results"]),
    () => ({ ...EMPTY_IMAGING_ROW })
  );
  const diagnoses = padRows(
    payload.diagnoses || [],
    10,
    (row) => present(row.code).length > 0 || present(row.label).length > 0,
    () => ({ code: "", label: "", selected: false })
  );
  const [leftDiagnoses, rightDiagnoses] = diagnosisColumns(diagnoses);

  return (
    <div className="face-sheet-print-page">
      <article className="space-y-4">
        <div className="face-sheet-print-shell">
          <div className="space-y-4">
            <table className="face-sheet-print-table">
              <colgroup>
                <col style={{ width: "34%" }} />
                <col style={{ width: "18%" }} />
                <col style={{ width: "30%" }} />
                <col style={{ width: "18%" }} />
              </colgroup>
              <tbody>
                <tr>
                  <td colSpan={2}>
                    <span className="face-sheet-print-label">NAME:</span> {present(patientSnapshot.full_name)}
                  </td>
                  <td colSpan={2}>
                    <span className="face-sheet-print-label">Care Manager:</span> {present(careContext.care_manager_name)}
                  </td>
                </tr>
                <tr>
                  <td colSpan={2}>
                    <span className="face-sheet-print-label">DOB/AGE:</span> {dobAge(patientSnapshot)}
                  </td>
                  <td colSpan={2}>
                    <span className="face-sheet-print-label">EMAIL:</span> {present(patientSnapshot.email)}
                  </td>
                </tr>
                <tr>
                  <td colSpan={2}>
                    <span className="face-sheet-print-label">CITY/STATE:</span> {cityState(patientSnapshot)}
                  </td>
                  <td>
                    <span className="face-sheet-print-label">CELL#:</span> {present(formatPhone(patientSnapshot.phone))}
                  </td>
                  <td>
                    <span className="face-sheet-print-label">Text Y/N:</span> {textOkValue(careContext)}
                  </td>
                </tr>
                <tr>
                  <td colSpan={2}>
                    <span className="face-sheet-print-label">ENDO:</span> {present(careContext.endocrinologist_name)}
                  </td>
                  <td colSpan={2}>
                    <span className="face-sheet-print-label">REFERRAL:</span> {present(careContext.referral_source)}
                  </td>
                </tr>
              </tbody>
            </table>

            <table className="face-sheet-print-table">
              <thead>
                <tr>
                  {LAB_COLUMNS.map((column) => (
                    <th key={column.key} className="face-sheet-print-head">
                      {column.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {labs.map((lab, index) => (
                  <tr key={`lab-row-${index}`}>
                    {LAB_COLUMNS.map((column) => (
                      <td key={`${column.key}-${index}`}>{formattedCellValue(lab[column.key], column.key)}</td>
                    ))}
                  </tr>
                ))}
                {operationSummary?.date ? (
                  <tr>
                    <td>{present(formatDate(operationSummary.date))}</td>
                    <td colSpan={LAB_COLUMNS.length - 1}>
                      Operated at {operationSummary.operationLocationLabel || "Unknown"}
                      {operationSummary.opNotePath ? " - Op Note" : ""}
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>

            <div className="grid grid-cols-2 gap-4">
              <ResultsTable title="TSH (uIU/mL)" rows={additionalResults.tsh || []} />
              <ResultsTable title="24HR URINE (mg/24 hr)" rows={additionalResults.urine_24h || []} />
            </div>
          </div>

          <section>
            <table className="face-sheet-print-table face-sheet-print-table-medications h-full">
              <thead>
                <tr>
                  <th className="face-sheet-print-head">MEDICATIONS</th>
                </tr>
              </thead>
              <tbody>
                {medications.map((medication, index) => (
                  <tr key={`medication-${index}`}>
                    <td>{medicationText(medication)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </div>

        <section className="space-y-2">
          <h3 className="face-sheet-print-heading">Imaging</h3>
          <table className="face-sheet-print-table">
            <colgroup>
              <col style={{ width: "16%" }} />
              <col style={{ width: "20%" }} />
              <col style={{ width: "64%" }} />
            </colgroup>
            <thead>
              <tr>
                <th className="face-sheet-print-head">DATE</th>
                <th className="face-sheet-print-head">IMAGE TYPE</th>
                <th className="face-sheet-print-head">RESULTS</th>
              </tr>
            </thead>
            <tbody>
              {imaging.map((image, index) => (
                <tr key={`imaging-${index}`}>
                  <td>{present(formatDate(image.date))}</td>
                  <td>{present(image.image_type)}</td>
                  <td>{present(image.results)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <div className="grid grid-cols-[14rem_minmax(0,1fr)] gap-4">
          <table className="face-sheet-print-table self-start">
            <tbody>
              <tr>
                <td className="face-sheet-print-label-cell">Consult date:</td>
                <td>{present(formatDate(careContext.consult_date))}</td>
              </tr>
              <tr>
                <td className="face-sheet-print-label-cell">CPT:</td>
                <td>{present(careContext.cpt_code)}</td>
              </tr>
            </tbody>
          </table>

          <section className="space-y-2">
            <h3 className="face-sheet-print-heading face-sheet-print-heading-left">ICD-10</h3>
            <div className="grid grid-cols-2 gap-4">
              <DiagnosisColumn diagnoses={leftDiagnoses} />
              <DiagnosisColumn diagnoses={rightDiagnoses} />
            </div>
          </section>
        </div>

        {present(payload.staff_notes) ? (
          <section className="space-y-2">
            <h3 className="face-sheet-print-heading face-sheet-print-heading-left">Staff Notes</h3>
            <div className="face-sheet-print-notes">{payload.staff_notes}</div>
          </section>
        ) : null}
      </article>
    </div>
  );
}
